/**
 * The one protocol every session-storage mutation goes through.
 *
 * WHY THIS EXISTS
 *
 * `localStorage` is shared by every tab on an origin but offers no atomic
 * compare-and-set spanning a read and a write, and each tab is a separate JS
 * realm, so an in-memory guard cannot serialize them. The old code relied on
 * doing its read → check → write inside one synchronous turn, which is only
 * atomic *within* a realm.
 *
 * That is not a theoretical gap. `REFRESH_REUSE_GRACE_MS` on the issuer is 60s,
 * so two tabs presenting the same refresh token inside that window BOTH succeed
 * and BOTH receive the same successor refresh token — while each response mints
 * its own distinct idToken. Concurrent cross-tab commits are a designed-for
 * occurrence, not an edge case.
 *
 * Two failures follow from an unlocked read-then-write, and both are closed here:
 *
 *   1. Lost update / resurrection. Tab A reads, tab B logs in (or logs out), tab A
 *      writes its stale blob back. B's login is silently replaced, or a logged-out
 *      session comes back to life.
 *   2. Spurious "the user changed". Tab B's commit finds tab A's blob, which
 *      matches neither B's snapshot nor B's own result because the idTokens
 *      differ — even though it is the same user and the same login.
 *
 * THE CRITICAL SECTION IS THE COMMIT, NOT THE NETWORK CALL
 *
 * The lock covers read → compare → write only. The refresh POST happens outside
 * it: holding a cross-tab lock across a 30s network call would serialize every
 * tab behind the slowest one. This is safe because the snapshot captured before
 * the network call is used only for COMPARISON. What actually gets written is
 * decided against storage re-read inside the lock, so a login or logout that lands
 * during the network call is always observed.
 *
 * NO BEST-EFFORT FALLBACK
 *
 * A `localStorage` lease was considered and rejected: its own acquire sequence is
 * a non-atomic read-then-write across realms, and a backgrounded tab can be frozen
 * past any lease timeout and then resume and write. Both leave exactly the windows
 * this module exists to close, so a browser without Web Locks fails closed instead.
 * Absence of `navigator.locks` does NOT imply a single realm — such a browser still
 * has tabs. Single realm is determined by the absence of `localStorage`.
 */
const SESSION_LOCK_NAME = "bounded_session_mutation";
export const UNSUPPORTED_ENVIRONMENT_CODE = "unsupported_environment";
/**
 * Thrown when authenticated session work is attempted in a browser that shares
 * `localStorage` across tabs but cannot serialize access to it. Reads and
 * anonymous requests are unaffected; only session mutation is refused.
 */
export class SessionEnvironmentError extends Error {
    constructor(message) {
        super(message);
        this.code = UNSUPPORTED_ENVIRONMENT_CODE;
        this.name = "SessionEnvironmentError";
    }
}
/**
 * True when nothing outside this realm can touch session storage.
 *
 * Keyed on `localStorage`, not on `window`: React Native defines `window` but
 * stores sessions through a per-process StorageAdapter, and Node/SSR has neither.
 * A browser is the only multi-realm case, and `localStorage` is what makes it one.
 */
function isSingleRealm() {
    return typeof localStorage === "undefined";
}
function hasWebLocks() {
    var _a;
    return typeof navigator !== "undefined"
        && typeof ((_a = navigator.locks) === null || _a === void 0 ? void 0 : _a.request) === "function";
}
/**
 * Run `fn` with exclusive access to session storage across every tab on this origin.
 *
 * Single-realm runtimes execute directly. Browsers serialize through Web Locks.
 * A browser without Web Locks throws rather than running unserialized.
 */
export async function withSessionLock(fn) {
    if (isSingleRealm()) {
        return await fn();
    }
    if (!hasWebLocks()) {
        throw new SessionEnvironmentError("This browser cannot safely coordinate a Bounded session across tabs " +
            "(the Web Locks API is unavailable). Authenticated operations are disabled; " +
            "anonymous reads still work.");
    }
    return await navigator.locks.request(SESSION_LOCK_NAME, { mode: "exclusive" }, async () => await fn());
}
/** Throws if this environment cannot safely mutate a session. Cheap pre-flight. */
export function assertSessionMutationSupported() {
    if (isSingleRealm() || hasWebLocks())
        return;
    throw new SessionEnvironmentError("This browser cannot safely coordinate a Bounded session across tabs " +
        "(the Web Locks API is unavailable). Authenticated operations are disabled; " +
        "anonymous reads still work.");
}
/**
 * Opaque per-login identifier stamped into the stored session.
 *
 * Minted on every `storeSession` (login, guest upgrade, wallet switch) and
 * PRESERVED across refresh commits. That is the whole point: it is what makes
 * "S1 refreshed into S1'" provable and distinguishable from "someone logged in
 * again", which token equality and address equality both get wrong — token
 * equality rejects a valid post-refresh retry, and address equality accepts a
 * same-wallet re-login as if it were the same session.
 *
 * The issuer has a server-side `familyId` that would serve, but `/session/refresh`
 * does not return it, so this is client-side.
 */
export function mintSessionGeneration() {
    const c = typeof crypto !== "undefined" ? crypto : undefined;
    if (typeof (c === null || c === void 0 ? void 0 : c.randomUUID) === "function")
        return c.randomUUID();
    if (typeof (c === null || c === void 0 ? void 0 : c.getRandomValues) === "function") {
        const bytes = c.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
    // Last resort only; every runtime the SDK targets provides one of the above.
    return `g${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
/**
 * Decide a commit against what storage ACTUALLY holds right now.
 *
 * Must be called inside `withSessionLock`, with `current` read inside that same
 * critical section — comparing against a value read before the network call is
 * exactly the race this exists to prevent.
 */
export function decideCommit(current, expected, next) {
    var _a;
    // Absence is SUPERSEDED, never "free to write". A logout that lands between a
    // refresh's read and its write must not be undone by that write. This is the
    // backstop that makes session resurrection impossible.
    if (!current)
        return "superseded";
    // A caller with no captured generation cannot prove lineage, so it must not
    // write. Reads mint a generation for legacy sessions before returning them, so
    // in practice this only catches a request that was in flight across that
    // upgrade. Fail closed: the caller retries and gets a provable session.
    if (expected.generation === null)
        return "superseded";
    // A different generation means a different LOGIN, which the address-based guard
    // cannot see when the same wallet signs in again.
    if (((_a = current.sessionGeneration) !== null && _a !== void 0 ? _a : null) !== expected.generation) {
        return "superseded";
    }
    if (current.idToken === next.idToken && current.refreshToken === next.refreshToken) {
        return "already-equal";
    }
    // Storage is exactly what this caller read before it went to the network, so
    // nothing has moved underneath and this write is the legitimate one.
    if (expected.idToken === undefined || current.idToken === expected.idToken) {
        return "applied";
    }
    // Same login, but storage has moved on: another caller's refresh of this same
    // session already landed. Theirs is the current truth — its rotated refresh
    // token is the live one — so defer rather than overwrite. Reporting a principal
    // change here would fail ordinary concurrent requests at expiry, which is the
    // common case, not a rare one.
    return "same-generation-winner";
}
